<template>
  <div
    :class="[
      $style.base,
      {
        [$style.smallScreen]: $screen === 's',
        [$style.largeScreen]: $screen === 'l'
      }
    ]"
  >
    <div :class="$style.inner">
      <div :class="$style.textContainer">
        <div :class="$style.header">
          <BaseText
            bold
            iconBefore="email"
            v-test="'email-template-name'"
          >
            <div :class="$style.title">
              {{ template.name || $t('marketing.email.templates.no_title') }}
            </div>
          </BaseText>
          <div :class="{ [$style.isSmall]: $screen === 's' }">
            <BaseLabel
              v-if="template.emailType === 'DEFAULT_MARKETING'"
              color="primary"
              v-test="'enabledStateLabel'"
            >
              {{ $t('global.default') }}
            </BaseLabel>
            <BaseLabel
              v-if="showRemainingDaysLabel"
              color="success"
              v-test="'remainingDaysLabel'"
              iconBefore="clock"
            >
              <BaseIcon
                name="clock"
                color="inverted"
                size="xs"
              />
              <div :class="$style.remainingDays">
                {{ filters.fromNow(template.deliverAt) }}
              </div>
            </BaseLabel>
          </div>
        </div>
        <div
          v-if="hasImage"
          :class="$style.visual"
          :style="{ 'background-image': `url(${template.imageUrl})` }"
          v-test="'email-template-image'"
        />
        <div
          v-if="template.subject"
          :class="$style.subject"
        >
          <BaseText
            oneLine
            v-test="'email-template-subject'"
          >
            {{ template.subject }}
          </BaseText>
        </div>
      </div>
      <div :class="$style.buttons">
        <BaseButton
          color="inverted"
          icon="delete"
          @click="deleteTemplate"
          v-test="'email-template-delete'"
        />
        <BaseButton
          :routerLink="{
            name: 'newsletter-template-edit',
            params: { id: template.id }
          }"
          color="inverted"
          @click="trackTemplate"
          v-test="'email-template-edit'"
        >
          {{ $t('marketing.email.templates.edit') }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import gql from 'graphql-tag';
import { defineComponent } from 'vue';
import dayjs from '@/dayjs';
import filters from '@/filters';
import { flash, modal } from '@/helpers/ui';
import { useContactEmailVerification } from '../../useContactEmailVerification';

export default defineComponent({
  props: {
    template: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { contactEmailVerified, verifyContactEmail } =
      useContactEmailVerification();
    return {
      contactEmailVerified,
      verifyContactEmail,
      filters,
    };
  },
  computed: {
    hasImage() {
      return this.template.hasImage && !!this.template.imageUrl;
    },

    showRemainingDaysLabel() {
      return (
        this.template?.hasDeliverySchedule &&
        dayjs().isBefore(dayjs(this.template.deliverAt))
      );
    },
  },
  methods: {
    trackTemplate() {
      if (!this.contactEmailVerified) {
        modal('confirmation', {
          type: 'sendEmail',
          subMessage: this.$t('email_templates.verification_modal.sub_message'),
          message: this.$t('email_templates.verification_modal.message'),
        }).then(() => {
          this.verifyContactEmail();
        });
      }
    },
    deleteTemplate() {
      modal('confirmation', {
        type: 'delete',
        item: this.$t('global.items.email_template'),
      }).then(() => {
        this.$apollo
          .mutate({
            mutation: gql`
              mutation deleteEmailTemplate($input: DeleteEmailTemplateInput!) {
                deleteEmailTemplate(input: $input) {
                  emailTemplate {
                    id
                  }
                }
              }
            `,
            variables: {
              input: {
                id: this.template.id,
              },
            },
            update(cache) {
              cache.evict({ id: 'ROOT_QUERY', fieldName: 'emailTemplates' });
              cache.gc();
            },
          })
          .then(() => {
            flash(this.$t('global.flash.template_deleted'));
          });
      });
    },
  },
});
</script>

<style
  lang="scss"
  module
>
p {
  margin: 0;
}
.base {
  padding: $spacing * 0.5;
  min-width: 250px;
  max-width: 400px;
  display: flex;

  &.largeScreen {
    width: 33.333%;
  }

  &.smallScreen {
    width: 100%;
  }

  &:not(.smallScreen):not(.largeScreen) {
    width: 50%;
  }
}

.textContainer {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  margin-bottom: $spacing;
}

.title {
  width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: $spacing * 0.5;
}

.subject {
  margin-bottom: $spacing;
}

.inner {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: var(--color-bg);
  border-radius: $radius;
  border: 1px solid var(--color-border);
  padding: $spacing;
  min-width: 0;
}

.visual {
  background-size: cover;
  background-position: center center;
  padding-bottom: 40%;
  margin-bottom: $spacing;
}

.buttons {
  display: flex;
  justify-content: flex-end;
  gap: $spacing;
}

.remainingDays {
  margin-left: 0.25rem;
}
</style>
